import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, X, Check, Upload, Loader2 } from 'lucide-react';
import PrimaryButton from './PrimaryButton';
import PrimaryButtonLight from './PrimaryButtonLight';
import { profileImageService } from '../../services/profile/profileImage.service';

interface UserProfileBadgeProps {
  userId: string;
  firstName: string;
  lastName: string;
  currentImageUrl?: string | null;
  onImageUpdate?: (imageUrl: string) => void;
  size?: 'small' | 'medium' | 'large';
  clickable?: boolean;
}

const UserProfileBadge: React.FC<UserProfileBadgeProps> = ({
  userId,
  firstName,
  lastName,
  currentImageUrl,
  onImageUpdate,
  size = 'medium',
  clickable = true
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(currentImageUrl || null);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 200, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Update croppedImage when currentImageUrl prop changes
  useEffect(() => {
    console.log('UserProfileBadge - currentImageUrl updated:', currentImageUrl);
    setCroppedImage(currentImageUrl || null);
  }, [currentImageUrl]);

  // Calculate badge dimensions based on size
  const getDimensions = () => {
    switch (size) {
      case 'small':
        return { size: 'w-8 h-8', text: 'text-xs' };
      case 'large':
        return { size: 'w-12 h-12', text: 'text-lg' };
      case 'medium':
      default:
        return { size: 'w-10 h-10', text: 'text-sm' };
    }
  };

  const dimensions = getDimensions();

  // Get user initials
  const getInitials = () => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const MIN_CROP_SIZE = 50;

  // Handle crop area drag (mouse + touch)
  const startDrag = (clientX: number, clientY: number) => {
    if (!imageRef.current) return;
    setIsDragging(true);
    const rect = imageRef.current.getBoundingClientRect();
    setDragStart({
      x: clientX - rect.left - cropArea.x,
      y: clientY - rect.top - cropArea.y
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    startDrag(e.clientX, e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY);
  };

  // Handle resize from corner handles
  const startResize = (clientX: number, clientY: number) => {
    setIsResizing(true);
    setResizeStart({ x: clientX, y: clientY, width: cropArea.width, height: cropArea.height });
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    startResize(e.clientX, e.clientY);
  };

  const handleResizeTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    startResize(touch.clientX, touch.clientY);
  };

  const moveResize = useCallback((clientX: number, clientY: number) => {
    if (!isResizing || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const dx = clientX - resizeStart.x;
    const dy = clientY - resizeStart.y;
    // Keep square: use the larger delta
    const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
    const newSize = Math.max(MIN_CROP_SIZE, resizeStart.width + delta);
    const maxW = rect.width - cropArea.x;
    const maxH = rect.height - cropArea.y;
    const clampedSize = Math.min(newSize, maxW, maxH);

    setCropArea(prev => ({ ...prev, width: clampedSize, height: clampedSize }));
  }, [isResizing, resizeStart, cropArea.x, cropArea.y]);

  const moveDrag = useCallback((clientX: number, clientY: number) => {
    if (!isDragging || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const newX = Math.max(0, Math.min(clientX - rect.left - dragStart.x, rect.width - cropArea.width));
    const newY = Math.max(0, Math.min(clientY - rect.top - dragStart.y, rect.height - cropArea.height));

    setCropArea(prev => ({ ...prev, x: newX, y: newY }));
  }, [isDragging, dragStart, cropArea.width, cropArea.height]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    moveDrag(e.clientX, e.clientY);
    moveResize(e.clientX, e.clientY);
  }, [moveDrag, moveResize]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    moveDrag(touch.clientX, touch.clientY);
    moveResize(touch.clientX, touch.clientY);
  }, [moveDrag, moveResize]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handlePointerUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handlePointerUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handlePointerUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handlePointerUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleTouchMove, handlePointerUp]);

  // Apply crop and save
  const handleApplyCrop = async () => {
    if (!imageRef.current || !canvasRef.current || !selectedImage) return;

    setIsUploading(true);
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = imageRef.current;
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;

      canvas.width = 200;
      canvas.height = 200;

      ctx.drawImage(
        img,
        cropArea.x * scaleX,
        cropArea.y * scaleY,
        cropArea.width * scaleX,
        cropArea.height * scaleY,
        0,
        0,
        200,
        200
      );

      canvas.toBlob(async (blob) => {
        if (!blob) return;

        try {
          // Validate userId before upload
          console.log('UserProfileBadge - attempting upload with userId:', userId);
          if (!userId || userId.trim() === '') {
            throw new Error('User ID is missing. Please sign out and sign in again.');
          }
          
          // Upload to backend
          const response = await profileImageService.uploadProfileImage(userId, blob, 'image/jpeg');
          
          if (response.data.success) {
            console.log('Upload successful, fetching fresh profile picture URL...');
            
            // Fetch the profile picture with a fresh pre-signed URL
            // This ensures we get a valid URL with proper CORS headers
            const profileData = await profileImageService.getProfileImage();
            
            if (profileData.hasProfilePicture && profileData.imageUrl) {
              console.log('Fresh profile picture URL retrieved:', profileData.imageUrl);
              setCroppedImage(profileData.imageUrl);
              
              // Notify parent component of the update
              if (onImageUpdate) {
                onImageUpdate(profileData.imageUrl);
              }
            }
          }
          
          setIsUploading(false);
          setShowModal(false);
          setSelectedImage(null);
        } catch (error) {
          console.error('Failed to upload profile image:', error);
          setIsUploading(false);
          // Show specific error message to the user
          const errorMessage = error instanceof Error ? error.message : 'Failed to upload profile picture. Please try again.';
          alert(errorMessage);
        }
      }, 'image/jpeg', 0.95);

    } catch (error) {
      console.error('Error cropping image:', error);
      setIsUploading(false);
    }
  };

  // Cancel and close modal
  const handleCancel = () => {
    setShowModal(false);
    setSelectedImage(null);
    setCropArea({ x: 0, y: 0, width: 200, height: 200 });
  };

  return (
    <>
      {/* Profile Badge */}
      <div
        className={`${dimensions.size} rounded-full overflow-hidden flex items-center justify-center ${
          clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
        } bg-slate-700 relative group`}
        onClick={() => {
          if (clickable) {
            if (!userId || userId.trim() === '') {
              alert('Unable to upload profile picture: User ID is missing. Please sign out and sign in again.');
              return;
            }
            setShowModal(true);
          }
        }}
        title={clickable ? 'Click to update profile picture' : undefined}
      >
        {croppedImage ? (
          <img
            src={croppedImage}
            alt={`${firstName} ${lastName}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className={`${dimensions.text} font-semibold text-white`}>
            {getInitials()}
          </span>
        )}
        {clickable && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
            <Camera className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={size === 'small' ? 14 : size === 'large' ? 24 : 18} />
          </div>
        )}
      </div>

      {/* Image Upload & Crop Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Update Profile Picture</h2>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                disabled={isUploading}
              >
                <X size={24} />
              </button>
            </div>

            {!selectedImage ? (
              // Upload Section
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                <p className="text-sm text-gray-400 mb-4">PNG, JPG, GIF up to 10MB</p>
                <PrimaryButton
                  onClick={() => fileInputRef.current?.click()}
                >
                  Select Image
                </PrimaryButton>
              </div>
            ) : (
              // Crop Section
              <div>
                <div className="mb-4 relative inline-block">
                  <img
                    ref={imageRef}
                    src={selectedImage}
                    alt="Selected"
                    className="max-w-full max-h-96 object-contain"
                  />
                  {/* Crop overlay */}
                  <div
                    className="absolute border-2 border-blue-500 cursor-move"
                    style={{
                      left: `${cropArea.x}px`,
                      top: `${cropArea.y}px`,
                      width: `${cropArea.width}px`,
                      height: `${cropArea.height}px`,
                      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                      touchAction: 'none'
                    }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                  >
                    <div className="absolute inset-0 border border-white opacity-50"></div>
                    {/* Resize handle — bottom-right corner */}
                    <div
                      className="absolute -bottom-2 -right-2 w-5 h-5 bg-blue-500 border-2 border-white rounded-sm cursor-nwse-resize"
                      style={{ touchAction: 'none' }}
                      onMouseDown={handleResizeMouseDown}
                      onTouchStart={handleResizeTouchStart}
                    />
                  </div>
                </div>

                {/* Hidden canvas for cropping */}
                <canvas ref={canvasRef} className="hidden" />

                <p className="text-sm text-gray-600 mb-4">
                  Drag the square to select the area you want to use
                </p>

                <div className="flex gap-3">
                  <PrimaryButtonLight
                    onClick={handleCancel}
                    disabled={isUploading}
                    className="flex-1"
                  >
                    Cancel
                  </PrimaryButtonLight>
                  <PrimaryButton
                    onClick={handleApplyCrop}
                    disabled={isUploading}
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Check size={18} />
                        Apply & Save
                      </>
                    )}
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UserProfileBadge;
